import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Tooltip, Button, Breadcrumbs, Link, Typography, TextField, Grid, FormControlLabel, Radio, RadioGroup, useMediaQuery, useTheme, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import { toast, ToastContainer } from 'react-toastify';
import Autocomplete from '@mui/material/Autocomplete';
import axiosInstance from '../../../lib/axios';
import { getRoomMasterdropdata } from '../../../Api/PropertyMst/Api';
import ResidentAccTable from './ResidentAccTable';
import { useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ResidentAcc = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerms, setSearchTerms] = useState({});
  const [singleSearchRows, setSingleSearchRows] = useState([]);
  const [multipleSearchRows, setMultipleSearchRows] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('All');
  const [property, setProperty] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchClicked, setSearchClicked] = useState(0);
  const [searchType, setSearchType] = useState('single');
  const [singleFilters, setSingleFilters] = useState({
    mobile: '',
    name: '',
    email: '',
    searchField: 'mobile',
  });
  const [multipleFilters, setMultipleFilters] = useState({
    checkIn: moment().format('YYYY-MM-DD'),
    checkOut: moment().format('YYYY-MM-DD'),
    status: 'CurrentOccupied',
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const printRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mobileFromUrl = params.get('mobile');

    if (mobileFromUrl) {
      setSearchType('single');
      setSingleFilters({
        searchField: 'mobile',
        mobile: mobileFromUrl,
        name: '',
        email: '',
      });
      setSearchClicked((prev) => prev + 1);
    }
  }, [location.search]);

  const fetchPropertyData = async () => {
    try {
      const userId = sessionStorage.getItem('UserId');
      const response = await getRoomMasterdropdata({
        Flag: '',
        UserId: userId,
      });

      if (response.data.Status === 0) {
        setProperty([{ Id: 'All', Name: 'All' }, ...response.data.Data]);
      }
    } catch (error) {
      console.error('Error fetching property data:', error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearchChange = (columnId, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [columnId]: value,
    }));
    setPage(0);
  };

  const handleSearch = async (type) => {
    if (type === 'single') {
      const field = singleFilters.searchField;
      if (!singleFilters[field] || singleFilters[field].trim() === '') {
        toast.error(`Please enter ${field === 'email' ? 'Email ID' : field.charAt(0).toUpperCase() + field.slice(1)}`);
        setSingleSearchRows([]);
        setSearchClicked(0);
        return;
      }
    }
    setSearchClicked((prev) => prev + 1);
    setLoading(true);
  };

  const handleSingleFilterChange = (field, value) => {
    if (field === 'searchField') {
      // Clear previous search results and reset search trigger when changing search field
      setSingleSearchRows([]);
      setSearchClicked(0);
      // Clear all search values to ensure clean state
      setSingleFilters((prev) => ({
        ...prev,
        searchField: value,
        mobile: '',
        name: '',
        email: '',
      }));
    } else {
      setSingleFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // const handleMultipleFilterChange = (field, value) => {

  //   setMultipleFilters((prev) => ({
  //     ...prev,
  //     [field]: value,
  //   }));
  //   if ((field === 'checkIn' && value === '') || (field === 'checkOut' && value === '')) {
  //     setSearchClicked(0);
  //     setMultipleSearchRows([]);
  //   }
  // };
  const handleMultipleFilterChange = (field, value) => {
    if (field === 'status') {
      // Clear previous search results and reset search trigger when changing status
      setMultipleSearchRows([]);
      setSearchClicked(0);
    }

    setMultipleFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    if ((field === 'checkIn' && value === '') || (field === 'checkOut' && value === '')) {
      setSearchClicked(0);
      setMultipleSearchRows([]);
    }
  };

  const handleSearchTypeChange = (event) => {
    const newSearchType = event.target.value;
    setSearchType(newSearchType);
    setPage(0);
    setSearchClicked(0);
    setSingleSearchRows([]);
    setMultipleSearchRows([]);
    if (newSearchType === 'single') {
      setSingleFilters({
        searchField: 'mobile',
        mobile: '',
        name: '',
        email: '',
      });
    }
  };

  const filteredRows = React.useMemo(() => {
    const rows = searchType === 'single' ? singleSearchRows : multipleSearchRows;
    if (searchClicked === 0) {
      return [];
    }
    return rows.filter((row) => {
      const matchesSearchTerms = Object.entries(searchTerms).every(([columnId, term]) => {
        if (!term) return true;
        const value = row[columnId]?.toString().toLowerCase() || '';
        return value.includes(term.toLowerCase());
      });

      const matchesProperty = selectedProperty === 'All' || row.PropId === selectedProperty;
      const matchesName = !singleFilters.name || row.Name?.toLowerCase().includes(singleFilters.name.toLowerCase());
      const matchesMobile = !singleFilters.mobile || row.Mobile?.includes(singleFilters.mobile);
      const matchesEmail = !singleFilters.email || row.Email?.toLowerCase().includes(singleFilters.email.toLowerCase());
      const matchesCheckIn = !multipleFilters.checkIn || moment(row.BookDt).isSameOrAfter(moment(multipleFilters.checkIn));
      const matchesCheckOut = !multipleFilters.checkOut || moment(row.BookDt).isSameOrBefore(moment(multipleFilters.checkOut));
      const matchesStatus = !multipleFilters.status || row.Status === multipleFilters.status;

      return (
        matchesSearchTerms &&
        matchesProperty &&
        (searchType === 'single' ? matchesName && matchesMobile && matchesEmail : matchesCheckIn && matchesCheckOut && matchesStatus)
      );
    });
  }, [singleSearchRows, multipleSearchRows, searchTerms, selectedProperty, singleFilters, multipleFilters, searchType, searchClicked]);

  useEffect(() => {
    fetchPropertyData();
  }, []);

  const handlePropertyChange = (event, value) => {
    const newValue = value ? (value.Id === 'All' ? '1' : value.Id) : '1';
    setSelectedProperty(newValue);
    setSearchClicked(0);
    setSingleSearchRows([]);
    setMultipleSearchRows([]);
  };

  const handleLocationclick = () => {
    navigate('/reports/bookdetails');
  };

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handleViewAccount = (rowData) => {
    setSelectedRow(rowData);
  };

  return (
    <Box
      sx={{
        maxWidth: '100%',
        width: '100%',
        overflowX: 'auto',
        backgroundColor: '#f9f9f9',
        minHeight: '100vh',
        p: { xs: 1, sm: 1 },
      }}
    >
      <ToastContainer />
      <Box
        sx={{
          backgroundColor: "#ffffff",
          padding: { xs: "8px 16px", sm: "12px 16px" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
          margin: "10px auto",
          maxWidth: "97%",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: 2,
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            display: "flex",
            flexDirection: { xs: "column", sm: "column", md: "row" }
          }}
        >
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <Box component="span">Home</Box>
          </Link>
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500" }}
            onClick={() => navigate('/reports/accountsFinance')}
          >
            Reports
          </Link>

          <Typography color="text.primary" sx={{ fontWeight: "500" }}>
            Resident Account
          </Typography>
        </Breadcrumbs>

        <Tooltip title='Previous Page' arrow>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1,
            justifyContent: "flex-start"
          }}
          >
            <IconButton
              onClick={() => navigate('/reports/accountsFinance')}
              sx={{
                backgroundColor: '#635bff',
                borderRadius: '50%',
                width: 35,
                height: 35,
                '&:hover': {
                  backgroundColor: '#635bff',
                },
                boxShadow: 3,
              }}
            >
              <ArrowBackIcon sx={{ color: 'white' }} />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
      <Box sx={{ maxWidth: 'calc(100% - 16px)', mx: { xs: 1, sm: 2 }, mt: 2 }}>
        <Grid container spacing={1} direction="column">
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1, sm: 1, md: 2 },
                  flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>Search Type:</Typography>
                <RadioGroup
                  row={!isMobile}
                  value={searchType}
                  onChange={handleSearchTypeChange}
                  sx={{
                    '& .MuiFormControlLabel-root': {
                      mr: { xs: 2, sm: 2 },
                      mb: { xs: 0, sm: 0 },
                    },
                    '& .MuiRadio-root': { p: '4px' },
                    flexDirection: { xs: 'row', sm: 'row' },
                  }}
                >
                  <FormControlLabel value="single" control={<Radio size="small" />} label="Single" />
                  <FormControlLabel value="multiple" control={<Radio size="small" />} label="Multiple" />
                </RadioGroup>
                {searchType === 'multiple' && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: 1,
                      mt: { xs: 1, sm: 0 },
                    }}
                  >
                    <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' }, minWidth: { sm: 50 } }}>
                      Status:
                    </Typography>
                    <RadioGroup
                      row={!isMobile}
                      value={multipleFilters.status}
                      onChange={(e) => handleMultipleFilterChange('status', e.target.value)}
                      sx={{
                        '& .MuiFormControlLabel-root': { mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } },
                        '& .MuiRadio-root': { p: '4px' },
                        flexDirection: { xs: 'column', sm: 'row' },
                      }}
                    >
                      <FormControlLabel value="CurrentOccupied" control={<Radio size="small" />} label="Current Occupied" />
                      <FormControlLabel value="PendingChecking" control={<Radio size="small" />} label="Pending CheckIn" />
                      <FormControlLabel value="CheckingPending" control={<Radio size="small" />} label="CheckOut" />
                    </RadioGroup>
                  </Box>
                )}
                {searchType === 'single' && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1, sm: 1, md: 2 },
                      mt: { xs: 0.5, sm: 0 },
                    }}
                  >
                    <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' }, minWidth: { sm: 80 } }}>
                      Search By Resident:
                    </Typography>
                    <RadioGroup
                      row={!isMobile}
                      value={singleFilters.searchField}
                      onChange={(e) => handleSingleFilterChange('searchField', e.target.value)}
                      sx={{
                        '& .MuiFormControlLabel-root': {
                          mr: { xs: 2, sm: 2 },
                          mb: { xs: 1, sm: 0 },
                        },
                        '& .MuiRadio-root': { p: '4px' },
                        flexDirection: { xs: 'row', sm: 'row' },
                      }}
                    >
                      <FormControlLabel value="mobile" control={<Radio size="small" />} label="Mobile" />
                      <FormControlLabel value="name" control={<Radio size="small" />} label="Name" />
                      <FormControlLabel value="email" control={<Radio size="small" />} label="Email ID" />
                    </RadioGroup>
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1,
                  flexWrap: 'wrap',
                  mt: 1,
                }}
              >
                <Box sx={{ width: { xs: '100%', sm: 250, md: 350 } }}>

                  <Autocomplete
                    options={property}
                    value={property.find((option) => option.Id === selectedProperty) || null}
                    onChange={handlePropertyChange}
                    getOptionLabel={(option) => option.Name || ''}
                    isOptionEqualToValue={(option, value) => (value ? option.Id === value.Id : option.Id === 'All')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Property"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    )}
                    sx={{
                      '& .MuiInputBase-root': { height: 35 },
                      '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem' } },
                    }}
                  />
                </Box>
                {searchType === 'single' && (
                  <>
                    {singleFilters.searchField === 'mobile' && (
                      <TextField
                        label="Search By Mobile"
                        type="tel"
                        variant="outlined"
                        size="small"
                        value={singleFilters.mobile}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            handleSingleFilterChange('mobile', value);
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          width: { xs: '100%', sm: 200, md: 350 },
                          '& .MuiInputBase-root': { height: 35 },
                          '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem' } },
                        }}
                      />
                    )}
                    {singleFilters.searchField === 'name' && (
                      <TextField
                        label="Search By Name"
                        type="text"
                        variant="outlined"
                        size="small"
                        value={singleFilters.name}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[a-zA-Z\s]*$/.test(value)) {
                            handleSingleFilterChange('name', value);
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          width: { xs: '100%', sm: 200, md: 350 },
                          '& .MuiInputBase-root': { height: 35 },
                          '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem' } },
                        }}
                      />
                    )}
                    {singleFilters.searchField === 'email' && (
                      <TextField
                        label="Search By Email ID"
                        type="email"
                        variant="outlined"
                        size="small"
                        value={singleFilters.email}
                        onChange={(e) => handleSingleFilterChange('email', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          width: { xs: '100%', sm: 200, md: 350 },
                          '& .MuiInputBase-root': { height: 35 },
                          '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem' } },
                        }}
                      />
                    )}
                  </>
                )}
                {searchType === 'multiple' && (
                  <>
                    <TextField
                      label="Check In"
                      type="date"
                      variant="outlined"
                      size="small"
                      value={multipleFilters.checkIn}
                      onChange={(e) => handleMultipleFilterChange('checkIn', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        width: { xs: '100%', sm: 150, md: 250 },
                        '& .MuiInputBase-root': { height: 35 },
                        '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem' } },
                      }}
                    />
                    <TextField
                      label="Check Out"
                      type="date"
                      variant="outlined"
                      size="small"
                      value={multipleFilters.checkOut}
                      onChange={(e) => handleMultipleFilterChange('checkOut', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        width: { xs: '100%', sm: 150, md: 250 },
                        '& .MuiInputBase-root': { height: 35 },
                        '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem' } },
                      }}
                    />
                  </>
                )}
                <Tooltip title="Search" arrow>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      backgroundColor: '#635bff',
                      color: 'white',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#dddbff',
                        color: '#635bff',
                      },
                      minWidth: 40,
                      height: 35,
                      p: 0,
                    }}
                    disabled={
                      searchType === 'single' &&
                      searchClicked === 0 &&
                      (!singleFilters[singleFilters.searchField] || singleFilters[singleFilters.searchField].trim() === '')
                    }
                    onClick={() => handleSearch(searchType)}
                  >
                    <SearchIcon />
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {searchClicked > 0 && (
        <Box sx={{ width: 'calc(100% - 16px)', mx: { xs: 1, sm: 2 }, mt: 2 }}>
          <ResidentAccTable
            searchBy={singleFilters.searchField}
            searchText={singleFilters[singleFilters.searchField]}
            selectedProperty={selectedProperty}
            fromDate={multipleFilters.checkIn}
            toDate={multipleFilters.checkOut}
            searchType={searchType}
            status={multipleFilters.status}
            triggerSearch={searchClicked}
            setRows={searchType === 'single' ? setSingleSearchRows : setMultipleSearchRows}
            setLoading={setLoading}
            onViewAccount={handleViewAccount}
            setSearchClicked={setSearchClicked}

          />
        </Box>
      )}
    </Box>
  );
};

export default ResidentAcc;